import React, { FC, ReactNode } from 'react'
import { BLOCKS, Block, Inline, INLINES } from '@contentful/rich-text-types'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import classNames from 'classnames'
import { kebabCase } from 'lodash'

import { Text } from 'marketing-site/src/library/elements/Text'
import { IProps } from './index'
import Checkmark from './components/Checkmark'

type Node = Block | Inline

const setOptions = (listStyle: string) => ({
  renderNode: {
    [BLOCKS.HEADING_1]: (node: Node, children: ReactNode) => (
      <h1 id={getId(children)}>
        <Text brand="default" size="heading-1-default">
          {children}
        </Text>
      </h1>
    ),
    [BLOCKS.HEADING_2]: (node: Node, children: ReactNode) => (
      <h2 id={getId(children)}>
        <Text brand="default" size="heading-2">
          {children}
        </Text>
      </h2>
    ),
    [BLOCKS.HEADING_3]: (node: Node, children: ReactNode) => (
      <h3 id={getId(children)}>
        <Text brand="default" size="heading-3">
          {children}
        </Text>
      </h3>
    ),
    [BLOCKS.HEADING_4]: (node: Node, children: ReactNode) => (
      <h4 id={getId(children)}>
        <Text brand="default" size="heading-4">
          {children}
        </Text>
      </h4>
    ),
    [BLOCKS.HEADING_5]: (node: Node, children: ReactNode) => (
      <h5 id={getId(children)}>
        <Text brand="default" size="heading-5">
          {children}
        </Text>
      </h5>
    ),
    [BLOCKS.HEADING_6]: (node: Node, children: ReactNode) => (
      <h6 id={getId(children)}>
        <Text brand="default" size="heading-6">
          {children}
        </Text>
      </h6>
    ),
    [BLOCKS.PARAGRAPH]: (node: Node, children: ReactNode) => {
      if (
        children &&
        Array.isArray(children) &&
        children.length <= 1 &&
        typeof children[0] === 'string' &&
        children[0].trim() === ''
      )
        return <br />
      return <p>{children}</p>
    },
    [INLINES.HYPERLINK]: (node: Node, children: ReactNode) => {
      const isInternalLink = node.data?.uri?.includes('intercom.com')
      const defaultTarget = isInternalLink ? '_self' : '_blank'
      const relation = isInternalLink ? '' : 'noopener noreferrer'
      const target = node.data?.target ? node.data.target : defaultTarget
      return (
        // TODO refactor target and rel attrs to clear this eslint error
        // eslint-disable-next-line react/jsx-no-target-blank
        <a id={getId(children)} href={node.data.uri} target={target} rel={relation}>
          {children}
        </a>
      )
    },
    [BLOCKS.UL_LIST]: (node: Node, children: ReactNode) => {
      return (
        <ul id={getId(children)} className={`ul-list ul-list--${listStyle}s`}>
          {children}
        </ul>
      )
    },
    [BLOCKS.LIST_ITEM]: (node: Node, children: ReactNode) => {
      return (
        <li id={getId(children)}>
          {listStyle === 'checkmark' && <Checkmark />}
          <Text brand="default" size="body">
            {children}
          </Text>
        </li>
      )
    },
  },
})

const getId = (children: ReactNode) => {
  if (Array.isArray(children) && typeof children[0] === 'string') {
    return kebabCase(children[0])
  }

  if (typeof children === 'string') {
    return kebabCase(children)
  }
}

const createHTMLWithKey = (html: string) => {
  return {
    __html: html,
  }
}

const render = (
  document: IProps['document'],
  html: IProps['html'],
  className: string,
  listStyle: string,
) => {
  if (document)
    return (
      <div className={className} data-testid="rich-text">
        {documentToReactComponents(document, setOptions(listStyle))}
      </div>
    )
  html = html?.replace(/<ul>/gi, `<ul class='ul-list ul-list--${listStyle}s'>`)
  /* eslint-disable react/no-danger */
  if (html)
    return (
      <div
        className={className}
        data-testid="rich-text"
        dangerouslySetInnerHTML={createHTMLWithKey(html)}
      />
    )
}

export const RichText: FC<IProps> = ({
  document,
  html,
  behaviour,
  listStyle = 'checkmark',
}: IProps) => {
  const className = classNames('rich-text', {
    [`rich-text__${behaviour?.toLowerCase()}`]: !!behaviour,
  })

  return (
    <>
      {render(document, html, className, listStyle)}

      <style jsx global>{`
        .rich-text {
          h1,
          h2 {
            font-family: $fontTTNormsPro;
            font-weight: bold;
            line-height: 1.2;
          }

          h1 {
            font-size: 26px;
          }

          h2 {
            font-size: 15px;
          }

          a {
            color: inherit;
            text-decoration: none;
            border-bottom: 1px solid;
          }

          a:hover,
          a:focus {
            outline: none;
            text-decoration: none;
            border-bottom: 1px solid;
          }

          b {
            font-weight: bold;
          }

          p {
            line-height: 1.65;

            &:not(:last-child) {
              margin-bottom: 1em;
            }
          }

          span {
            word-break: initial;
          }

          ul,
          ol {
            margin: 0;
            padding-left: 1.625em;

            li {
              margin-bottom: 1em;

              &:last-child {
                margin-bottom: 0;
              }
            }

            p {
              display: inline-block;
            }

            & + br {
              display: none;
            }
          }

          .ul-list {
            font-size: 1rem;

            &--checkmarks {
              padding-left: 0;

              li {
                display: flex;
                align-items: start;
                gap: 0.625rem;

                svg {
                  flex-shrink: 0;
                  width: 1rem;
                  height: auto;
                  margin-top: 0.25rem;
                }

                svg + span {
                  display: inline-block;
                }
              }
            }

            &--bullets {
              li {
                list-style: disc;
              }

              &::marker {
                text-align: center;
              }
            }
          }

          ol {
            li {
              list-style-type: decimal;
            }

            svg {
              display: none;
            }
          }

          & > br {
            display: inline-block;
            width: 100%;
            height: 1em;
            margin-bottom: 1em;
            content: '';
          }
        }

        .rich-text__typography {
          &:not(:first-child) {
            margin-top: 3em;
          }

          h1,
          h2,
          h3,
          h4,
          h5,
          h6 {
            font-size: 100%;
            scroll-margin-top: 130px;
            margin-bottom: 1rem;

            &:not(:first-child) {
              margin-top: 2rem;
            }
          }

          h1 {
            font-size: 2.5rem;
            margin-top: 4.5rem;
          }

          h2 {
            font-size: 2rem;
            margin-top: 4rem;
          }

          h3 {
            font-size: 1.5rem;
          }

          h4 {
            font-size: 1.2rem;
          }

          h5 {
            font-size: 1rem;
          }

          h6 {
            font-size: 0.8rem;
          }

          ul,
          ol {
            margin: 1rem 0;
          }
        }
      `}</style>
    </>
  )
}
